import {
  returnAllTradeDetailsFromGenericParams,
} from "@/lib/agGridColDefs/colDefGlobalHelpers";
import {
  containsHyphenInQuote,
  formatDollarFractionString,
} from "@/lib/businessRules/dollarFractionBusinessRule";
import {
  formatValueWithNotation,
  returnDecimalFormatting,
  isPriceNotation,
} from "@/lib/globalFormatterHelper";
import { resolveNotationFromParams } from "@/lib/currencySymbolHelper";
import { ExecutionStrategy } from "@/lib/interfaces and Enums/InterfaceExecutionStrategy";
import tradeDataStore from "@/store/tradeDataStore";
import { getOrderType, getReferencePrice, getNotation } from "./levelHelpers";

/**
 * Pure formatting function for level cell VALUE ONLY (for copy/paste)
 * Returns the value WITHOUT notation suffix
 *
 * Logic:
 * - If orderType is "Market" -> return "Market"
 * - If orderType is NOT "Market" -> return referencePrice value
 *
 * Note: API now returns values in display format (no multiplier needed)
 *
 * @param params - AG Grid cell params
 * @param currentTrade - The current trade from Zustand store (optional for valueFormatter)
 * @returns Formatted string - either "Market" or the referencePrice value
 */
export function formatLevelValue(
  params: any,
  currentTrade?: ExecutionStrategy,
): string {
  const orderType = getOrderType(params, currentTrade);

  if (orderType === "Market") {
    return "Market";
  }

  const referencePrice = getReferencePrice(params, currentTrade);
  const notation = getNotation(params, currentTrade);

  if (
    referencePrice !== null &&
    referencePrice !== undefined &&
    referencePrice !== ""
  ) {
    // $fr: return the fraction string as-is (no parseFloat)
    if (
      notation === "$fr" &&
      typeof referencePrice === "string" &&
      containsHyphenInQuote(referencePrice)
    ) {
      return formatDollarFractionString(referencePrice);
    }

    const numValue =
      typeof referencePrice === "string"
        ? parseFloat(referencePrice)
        : referencePrice;
    if (!isNaN(numValue)) {
      const roundedValue = returnDecimalFormatting(numValue, notation, true);
      return roundedValue || "";
    }
  }

  return "";
}

/**
 * Formatting function for level cell DISPLAY (with notation suffix)
 * Returns the value WITH notation suffix (e.g., "15 bps")
 *
 * Note: API now returns values in display format (no multiplier needed)
 *
 * @param params - AG Grid cell params
 * @param currentTrade - The current trade from Zustand store
 * @returns Formatted string - either "Market" or "value + notation" (e.g., "15 bps")
 */
export function formatLevelValueWithNotation(
  params: any,
  currentTrade?: ExecutionStrategy,
): string {
  const orderType = getOrderType(params, currentTrade);
  const notation = getNotation(params, currentTrade);
  const { isLeg } = returnAllTradeDetailsFromGenericParams(params);

  if (orderType === "Market") {
    return "Market";
  }

  // Resolve "$" to actual currency symbol (£, €, ¥, AUD, etc.)
  const displayNotation = resolveNotationFromParams(notation, params, currentTrade);

  const referencePrice = getReferencePrice(params, currentTrade);

  if (
    referencePrice !== null &&
    referencePrice !== undefined &&
    referencePrice !== ""
  ) {
    // $fr: format the fraction string and append notation
    if (
      notation === "$fr" &&
      typeof referencePrice === "string" &&
      containsHyphenInQuote(referencePrice)
    ) {
      return formatDollarFractionString(referencePrice);
    }

    const numValue =
      typeof referencePrice === "string"
        ? parseFloat(referencePrice)
        : referencePrice;
    if (!isNaN(numValue)) {
      const roundedValue = returnDecimalFormatting(numValue, notation, true);
      if (roundedValue) {
        return formatValueWithNotation(roundedValue, displayNotation);
      }
    }
  }

  // At LEG level: return empty string when no value
  // At ER level: return notation as placeholder when no value
  // $p/$fr: never show a placeholder in the Level field
  if (isLeg || isPriceNotation(notation)) {
    return "";
  }

  return displayNotation || "";
}

/**
 * AG Grid valueFormatter for copy/paste functionality
 * This ensures copied values match what's displayed by accessing Zustand store
 */
export function levelValueFormatter(params: any): string {
  // Access Zustand store directly to get current trade data
  const { fullTradeData } = returnAllTradeDetailsFromGenericParams(params);
  const currentTradeData = tradeDataStore.getState().tradeData;
  const thisTrade = currentTradeData.find(
    (trade) => trade.esid == fullTradeData.esid,
  );

  return formatLevelValue(params, thisTrade);
}
